import { History, Loader2, Send, UserRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { userAPI } from '../services/api';
import { apiErrorDetail, responseData } from '../services/apiResponse';

export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [transferError, setTransferError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reload, setReload] = useState(0);
  const transferPending = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setContact(null);
    setLoadError('');
    setTransferError('');
    userAPI.getContact(contactId).then((response) => {
      const saved = responseData(response);
      if (!saved?.contact_id) throw new Error('Missing contact details');
      if (active) setContact(saved);
    }).catch((error) => {
      if (active) setLoadError(apiErrorDetail(error, 'Contact details could not be loaded. Please try again.'));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [contactId, reload]);

  const handleTransfer = async () => {
    if (!contact || transferPending.current) return;
    transferPending.current = true;
    setSubmitting(true);
    setTransferError('');
    try {
      const response = await userAPI.createContactTransfer(contact.contact_id);
      const requestId = responseData(response)?.request_id;
      if (!requestId) throw new Error('Missing transfer request');
      navigate(`/review/${encodeURIComponent(requestId)}`);
    } catch (error) {
      setTransferError(apiErrorDetail(error, 'Failed to start transfer. Please try again.'));
    } finally {
      transferPending.current = false;
      setSubmitting(false);
    }
  };

  const details = contact ? [
    ['Phone', contact.phone],
    ['Email', contact.email],
    ['Date of birth', contact.date_of_birth],
    ['Address', contact.address],
  ] : [];

  return (
    <Layout onBack={() => navigate('/?tab=contacts')} backLabel="Back to contacts">
      {loading ? (
        <div role="status" className="flex min-h-64 items-center justify-center gap-3 text-sm text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin text-cyan-700" aria-hidden="true" />
          Loading contact…
        </div>
      ) : loadError ? (
        <div className="py-8">
          <h1 className="text-xl font-semibold text-slate-900">Contact details</h1>
          <p role="alert" className="mt-4 rounded bg-red-50 px-3 py-3 text-sm text-red-700">{loadError}</p>
          <button type="button" onClick={() => setReload((value) => value + 1)} className="mt-4 rounded px-3 py-2 text-sm font-semibold text-cyan-800 hover:bg-cyan-50">Try again</button>
        </div>
      ) : contact ? (
        <article aria-labelledby="contact-name">
          <header className="flex items-start gap-4 border-b border-slate-200 pb-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-cyan-50 text-cyan-800"><UserRound className="h-6 w-6" aria-hidden="true" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Contact details</p>
              <h1 id="contact-name" className="mt-1 break-words text-2xl font-semibold text-slate-900">{contact.full_name}</h1>
            </div>
          </header>

          <section aria-labelledby="contact-information" className="py-6">
            <h2 id="contact-information" className="text-sm font-semibold text-slate-900">Contact information</h2>
            <dl className="mt-4 divide-y divide-slate-100">
              {details.map(([label, value]) => (
                <div key={label} className="grid gap-1 py-3 first:pt-0 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                  <dt className="text-sm text-slate-500">{label}</dt>
                  <dd className="break-words text-sm font-medium text-slate-800">{value || 'Not provided'}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section aria-labelledby="receiving-wallet" className="border-t border-slate-200 py-6">
            <h2 id="receiving-wallet" className="text-sm font-semibold text-slate-900">Receiving wallet</h2>
            <dl className="mt-4">
              <div>
                <dt className="text-xs text-slate-500">Wallet address</dt>
                <dd className="mt-2 break-all border-l-2 border-cyan-700 bg-slate-50 px-3 py-3 font-mono text-sm text-slate-800">{contact.wallet_address || 'Not provided'}</dd>
              </div>
            </dl>
          </section>

          <footer className="border-t border-slate-200 pt-5">
            {transferError ? <p role="alert" className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{transferError}</p> : null}
            <button type="button" onClick={handleTransfer} disabled={submitting} className="flex min-h-11 w-full items-center justify-center gap-2 rounded bg-cyan-700 px-4 py-3 text-sm font-semibold text-white hover:bg-cyan-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
              {submitting ? 'Starting transfer…' : 'Transfer'}
            </button>
            <button type="button" onClick={() => navigate(`/?contact_id=${encodeURIComponent(contact.contact_id)}&period=all`)} className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-2">
              <History className="h-4 w-4" aria-hidden="true" />
              View transfer history
            </button>
          </footer>
        </article>
      ) : null}
    </Layout>
  );
}
